/**
 * Timeline View — War Room Phase C.4
 *
 * Chronological feed of all deliberation events.
 * Filterable by agent. Each event shows agent avatar, event type icon,
 * tool name, result summary, and reasoning.
 */

import React, { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { displayText } from '@/lib/displayText';
import {
  Wrench,
  Search,
  MessageSquare,
  AlertTriangle,
  RotateCw,
  Target,
  Filter,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  type DeliberationEvent,
  type RagChunk,
  getAgentDisplayName,
  getAgentColor,
  getEventTypeLabel,
  formatCurrency,
} from '@/services/deliberationApi';

// =============================================================================
// TYPES
// =============================================================================

export interface TimelineViewProps {
  events: DeliberationEvent[];
}

// =============================================================================
// HELPERS
// =============================================================================

function getEventIcon(eventType: string) {
  switch (eventType) {
    case 'tool_call':
      return Wrench;
    case 'finding':
      return Search;
    case 'position':
      return Target;
    case 'disagreement':
      return AlertTriangle;
    case 'human_input':
      return MessageSquare;
    case 're_analysis':
      return RotateCw;
    default:
      return Search;
  }
}

function getEventBg(eventType: string): React.CSSProperties {
  switch (eventType) {
    case 'tool_call':
      return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'finding':
      return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'position':
      return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'disagreement':
      return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'human_input':
      return { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' };
    case 're_analysis':
      return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    default:
      return { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' };
  }
}

function getAgentDotColor(agentName: string): React.CSSProperties {
  const color = getAgentColor(agentName);
  const map: Record<string, React.CSSProperties> = {
    blue: { backgroundColor: 'var(--color-brand)' },
    purple: { backgroundColor: 'var(--color-brand)' },
    amber: { backgroundColor: 'var(--color-brand)' },
    green: { backgroundColor: 'var(--color-verified)' },
    gray: { backgroundColor: 'var(--color-text-muted)' },
  };
  return map[color] || map.gray;
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// =============================================================================
// RAG CITATIONS SUB-COMPONENT
// =============================================================================

function getSimilarityColor(similarity: number): React.CSSProperties {
  if (similarity >= 0.85) return { backgroundColor: 'var(--color-brand)', color: 'var(--color-needs-human-text)' };
  if (similarity >= 0.70) return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
  return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
}

function RagCitations({ chunks }: { chunks: RagChunk[] }) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        {chunks.map((chunk, i) => {
          const label = chunk.source_file.replace(/\.pdf$/i, '').replace(/_/g, ' ');
          const simColor = getSimilarityColor(chunk.similarity);
          const isExpanded = expandedIdx === i;

          return (
            <button
              key={i}
              onClick={() => setExpandedIdx(isExpanded ? null : i)}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs transition-colors"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}
            >
              <FileText className="h-3 w-3" style={{ color: 'var(--color-text-dim)' }} />
              <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
              <span className="px-1.5 py-px rounded-full text-[10px] font-semibold" style={simColor}>
                {Math.round(chunk.similarity * 100)}%
              </span>
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" style={{ color: 'var(--color-text-dim)' }} />
              ) : (
                <ChevronDown className="h-3 w-3" style={{ color: 'var(--color-text-dim)' }} />
              )}
            </button>
          );
        })}
      </div>
      {expandedIdx !== null && chunks[expandedIdx] && (
        <div className="rounded-md border p-2.5 text-xs leading-relaxed" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)' }}>
          {chunks[expandedIdx].content}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function TimelineView({ events }: TimelineViewProps) {
  const [agentFilter, setAgentFilter] = useState<string | null>(null);

  // Get unique agent names for filter
  const agents = useMemo(() => {
    const names = new Set(events.map((e) => e.agent_name));
    return Array.from(names).sort();
  }, [events]);

  // Apply filter
  const filteredEvents = useMemo(() => {
    if (!agentFilter) return events;
    return events.filter((e) => e.agent_name === agentFilter);
  }, [events, agentFilter]);

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4" style={{ color: 'var(--color-text-dim)' }} />
        <button
          onClick={() => setAgentFilter(null)}
          className="px-3 py-1 text-xs rounded-full border transition-colors"
          style={
            agentFilter === null
              ? { backgroundColor: 'var(--color-text-primary)', color: 'var(--color-bg-surface)', borderColor: 'var(--color-text-primary)' }
              : { backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }
          }
        >
          All ({events.length})
        </button>
        {agents.map((agent) => (
          <button
            key={agent}
            onClick={() => setAgentFilter(agentFilter === agent ? null : agent)}
            className="px-3 py-1 text-xs rounded-full border transition-colors"
            style={
              agentFilter === agent
                ? { backgroundColor: 'var(--color-text-primary)', color: 'var(--color-bg-surface)', borderColor: 'var(--color-text-primary)' }
                : { backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }
            }
          >
            {getAgentDisplayName(agent)} ({events.filter((e) => e.agent_name === agent).length})
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-px" style={{ backgroundColor: 'var(--color-border)' }} />

        <div className="space-y-3">
          {filteredEvents.map((event, idx) => {
            const EventIcon = getEventIcon(event.event_type);
            const eventBg = getEventBg(event.event_type);
            const dotColor = getAgentDotColor(event.agent_name);
            const summary = displayText(event.tool_result_summary);
            const reasoning = displayText(event.reasoning);

            return (
              <div key={idx} className="relative flex items-start gap-4 pl-2">
                {/* Timeline dot */}
                <div className="relative z-10 h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={dotColor}>
                  <EventIcon className="h-3 w-3" style={{ color: 'var(--color-on-brand)' }} />
                </div>

                {/* Event card */}
                <div className="flex-1 rounded-lg border p-3" style={eventBg}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {getAgentDisplayName(event.agent_name)}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {getEventTypeLabel(event.event_type)}
                      </Badge>
                      {event.tool_name && (
                        <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>
                          {event.tool_name}
                        </span>
                      )}
                    </div>
                    <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
                      {formatTimestamp(event.timestamp)}
                    </span>
                  </div>

                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{summary}</p>

                  {event.tool_name === 'query_war_room_intelligence' && event.tool_result_data?.chunks?.length > 0 && (
                    <RagCitations chunks={event.tool_result_data.chunks} />
                  )}

                  {reasoning && reasoning !== summary && (
                    <p className="text-xs mt-1 italic" style={{ color: 'var(--color-text-muted)' }}>{reasoning}</p>
                  )}

                  {/* Value and confidence */}
                  <div className="flex items-center gap-3 mt-2">
                    {event.value && (
                      <span className="text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {formatCurrency(event.value)}
                      </span>
                    )}
                    {typeof event.confidence === 'number' && !isNaN(event.confidence) && (
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {Math.round(event.confidence * 100)}% confidence
                      </span>
                    )}
                    {event.issue_tag && (
                      <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>#{event.issue_tag}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-8 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          No events to display.
        </div>
      )}
    </div>
  );
}

export default TimelineView;
